<script setup lang="ts">
import type { UserBranding } from '@/api/queries/user/types/user';
import type { Assets, Avatar } from '@/features/avatar/types';
import { computed, inject } from 'vue';
import { storeToRefs } from 'pinia';
import { useAvatarStore } from '@/features/avatar/avatarStore';
import lazyAvatar from '@/assets/img/avatar-default.png';
import UnicornIcon from '@/components/icons/UnicornIcon.vue';

type Props = {
  branding?: UserBranding;
  companyLogo?: string;
  size?: number;
  gradient?: boolean;
  empty?: boolean;
  hasOuterCircle?: boolean;
  imgSize?: 'card' | 'large' | 'medium' | 'mini' | 'small' | 'thumb';
  iconName?: string;
};

const props = withDefaults(defineProps<Props>(), {
  size: 44,
  gradient: true,
  empty: false,
  imgSize: 'card',
});

const { isAvatarLoading } = storeToRefs(useAvatarStore());

// @todo: add alt text to img tag
const avatar = inject('avatar') as Avatar;
const assets = inject('assets') as Assets;
const gradients = props.branding?.gradients ?? ['#DBDFE1', '#DBDFE1'];

const styleContainer = computed(() => {
  return {
    width: `${props.size}px`,
    minWidth: `${props.size}px`,
    height: `${props.size}px`,
    padding: props.gradient ? `${props.size / 20}px` : '0px',
    background: props.gradient ? `linear-gradient(to right, ${gradients.join(', ')})` : '#fff',
    borderRadius: `${props.size / 2}px`,
  };
});

const avatarImg = computed(() => {
  // if (props.empty || isAvatarLoading.value) return lazyAvatar;
  if (props.empty || isAvatarLoading.value || props.iconName) return null;

  if (props.branding?.id) return avatar(props.branding?.id, props.imgSize);

  // if (props.branding?.avatar) return assets(props.branding?.avatar);
  if (props.branding?.avatar) return null;

  if (props.companyLogo) return assets(props.companyLogo);

  return lazyAvatar;
});

const styleOuterCircle = computed(() => {
  return {
    width: `${props.size + 30}px`,
    height: `${props.size + 30}px`,
  };
});
</script>

<template>
  <div class="relative flex aspect-square" :style="styleContainer">
    <div
      v-if="hasOuterCircle"
      class="absolute border -translate-x-2/4 -translate-y-2/4 rounded-full border-solid left-2/4 top-2/4"
      :style="styleOuterCircle"
    ></div>
    <v-img
      v-if="avatarImg"
      class="rounded-circle"
      role="button"
      :aria-label="$t('accessibility.menu.general')"
      :src="avatarImg"
      :lazy-src="lazyAvatar"
      alt=""
    ></v-img>

    <div v-else-if="props.iconName" class="absolute w-[90%] h-[90%] rounded-full bg-af-grey-300">
      <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
        <v-icon
          color="af-grey-600"
          :icon="props.iconName"
          :size="40"
          :aria-label="props.iconName"
        />
        <p class="text-body-4 mt-2">{{ $t('general.upload') }}</p>
      </div>
    </div>

    <UnicornIcon
      v-else
      :gradient="props.branding?.gradients ? styleContainer.background : undefined"
    />
  </div>
</template>
